import React, { useEffect, useMemo, useState } from "react";

import _ from "lodash";
import { useSelector } from "react-redux";
import { v4 as uuidv4 } from 'uuid';
import { svg } from "../assets/svg";
import { components } from "../components";
import ComboLevelDialog from "../components/ComboLevelDialog";
import Enums from "../helpers/enums";
import utils from "../helpers/utils";
import { hooks } from "../hooks";
import { getCustomizeItems } from "../screens/CustomizeHelper";
import apiService from "../service/ApiService";
import { RootState } from "../store";
import { addToCart } from "../store/slices/cartSlice";
import { CartType, CategoryItemType } from "../types";
import { actions } from "../store/actions";
import { Routes, TabScreens } from "../routes";

type Props = {
  categoryItem: CategoryItemType;
  isLast: boolean;
  isShowCalory: boolean
};

export const MenuListItem: React.FC<Props> = ({ categoryItem, isLast, isShowCalory }) => {
  const navigate = hooks.useNavigate();
  const dispatch = hooks.useDispatch();
  const [isComboLevelLoading, setIsComboLevelLoading] = useState(false);
  const [comboItem, setComboItem] = useState<CategoryItemType | null>(null);
  const [comboLevels, setComboLevels] = useState([]);
  const [combolevelDialogOpen, setCombolevelDialogOpen] = useState(false);
  const [error, setError] = useState<string>('');
  const [state, setState] = useState<any>({
    items: [],
    description: '',
    selectedItems: [],
    swappedItems: [],
  });

  const selectedSiteId = useSelector((state: RootState) => state.selectedSiteReducer.selectedSiteId);
  const cartItems = useSelector((state: RootState) => state.cartReducer.cartItems);
  const isDelivery = useSelector((state: RootState) => state.cartReducer.isDelivery);
  const config = useSelector((state: RootState) => state.appReducer.config);
  const itemCustomizations = useSelector((state: RootState) => state.lookupReducer.itemCustomizations);

  async function getComboItems(comboLevelId?: number) {
    if (comboLevelId !== undefined && comboLevelId !== null) {
      setIsComboLevelLoading(true);
      let isPack = false;
      const payload = {
        comboLevelId: comboLevelId,
        refId: categoryItem?.refId ?? 0,
        menuId: categoryItem?.menuId ?? null,
      };
      const { data } = await apiService.getComboItemsAsync(selectedSiteId, categoryItem?.menuItemId ?? 0, payload, isDelivery, isPack);
      if (data) {
        let filteredItems =
          categoryItem?.items && _.size(categoryItem.items) > 0
            ? [...categoryItem.items]
            : _.filter(data.items, (i) => i.itemGroupId === 1);

        setState((prev: any) => ({
          ...prev,
          items: data.items,
          selectedItems: filteredItems,
        }));
      }
      setIsComboLevelLoading(false);
    }
  };

  const { totalCustomizationPrice } = React.useMemo(() => {
    let customizations = state.selectedItems.reduce((accumulator: any, currentItem: any) => {
      if (currentItem.customizations) {
        accumulator = accumulator.concat(currentItem.customizations);
      }
      return accumulator;
    }, []);
    customizations = _.filter(customizations, ['modCodeId', 1]);
    const totalCustomizationPrice = _.sumBy(customizations, 'price');
    return {
      totalCustomizationPrice,
    };
  }, [state.selectedItems]);

  const loadMealDataReducer = (): CartType | null => {
    if (!comboItem) {
      return null;
    }
    const payload: CartType = {
      _id: comboItem?._id,
      menuItemId: comboItem?.menuItemId,
      quantity: comboItem?.quantity,
      maxQty: comboItem?.maxQty,
      promoId: comboItem?.promoId,
      price: comboItem?.price,
      totalCutomizedItemPrice: totalCustomizationPrice,
      caloricValue: _.sumBy(state.selectedItems, 'caloricValue'),
      imagePath: comboItem?.imagePath,
      type: Enums.comboType.quickCombo,
      swappedPrice: 0,
      name: comboItem?.name,
      comboSize: comboItem?.comboSize,
      items: state.selectedItems,
      comboLevelId: comboItem?.comboLevelId,
      refId: comboItem?.refId ? categoryItem.refId : 0,
      menuId: comboItem?.menuId ? categoryItem.menuId : null,
      isMandatory: state.items && state.items[0] && state.items[0].isMandatory ? state.items[0].isMandatory : false
    };
    return payload;
  };

  const totalQty = useMemo(() => {
    const itemQty = cartItems.find((c) => c.menuItemId === categoryItem.menuItemId);
    return itemQty ? itemQty.quantity ?? 0 : 0;
  }, [cartItems]);

  const onPressItemAddToCart = async () => {
    const payload = createAddToCartPayload();
    if (payload) {
      if (isAddToCartAllowed()) {
        if (config.canShowCustomizations || (categoryItem && categoryItem.isMandatory)) {
          await getCustomizeItems({
            item: payload,
            siteId: selectedSiteId,
            isDelivery: isDelivery,
            dispatch: dispatch,
            itemCustomizations: itemCustomizations,
            navigate: navigate,
            isMandatoryCustomization: categoryItem && categoryItem.isMandatory,
            showSkip: false,
            isUpdateCart: false
          });
        } else {
          dispatch(addToCart(payload));
          dispatch(actions.setScreen(TabScreens.Order));
          navigate(Routes.TabNavigator);
        }
      } else {
        setError(`Sorry, you cannot add ${payload.name} more than ${categoryItem?.maxQty} times`);
      }
    }
  };

  const onPressComboAddToCart = async () => {
    const payload = loadMealDataReducer();
    if (payload) {
      if (isAddToCartAllowed()) {
        setCombolevelDialogOpen(false);
        if (config.canShowCustomizations || (state.items && state.items[0] && state.items[0].isMandatory)) {
          await getCustomizeItems({
            item: payload,
            siteId: selectedSiteId,
            isDelivery: isDelivery,
            dispatch: dispatch,
            itemCustomizations: itemCustomizations,
            navigate: navigate,
            isMandatoryCustomization: state.items && state.items[0] && state.items[0].isMandatory,
            showSkip: false,
            isUpdateCart: false
          });
        } else {
          dispatch(addToCart(payload));
          dispatch(actions.setScreen(TabScreens.Order));
          navigate(Routes.TabNavigator);
        }
      } else {
        setError(`Sorry, you cannot add ${payload.name} more than ${categoryItem?.maxQty} times`);
      }
    }
  };

  const onAddCart = async () => {
    if (categoryItem && categoryItem.salesItemId) {
      onPressItemAddToCart();
    } else {
      setIsComboLevelLoading(true);
      const combo = { ...categoryItem, _id: uuidv4(), quantity: 1 };
      setComboItem(combo);

      const data = categoryItem.menuId
        ? await apiService.getOrderkeyComboLevelsAsync(selectedSiteId, categoryItem.menuId, categoryItem?.menuItemId ?? 0, isDelivery)
        : await apiService.getComboLevelsAsync(selectedSiteId, categoryItem?.menuItemId ?? 0, isDelivery);

      if (data && data.data) {
        setComboLevels(data.data.levels);
        if (data.data.levels.length === 1) {
          const selectedCombo = data.data.levels[0];
          const combo = {
            ...categoryItem,
            _id: uuidv4(),
            quantity: 1,
            comboSize: selectedCombo.comboSize,
            comboLevelId: selectedCombo.comboLevelId,
            price: selectedCombo.accumulatedPrice,
          };
          setComboItem(combo);

          const itemsData = await apiService.getComboItemsAsync(
            selectedSiteId,
            categoryItem?.menuItemId ?? 0,
            {
              comboLevelId: selectedCombo.comboLevelId,
              refId: categoryItem?.refId ?? 0,
              menuId: categoryItem?.menuId ?? null,
            },
            isDelivery,
            false
          );

          if (itemsData?.data) {
            const filteredItems = categoryItem?.items && _.size(categoryItem.items) > 0
              ? [...categoryItem.items]
              : _.filter(itemsData.data.items, (i) => i.itemGroupId === 1);

            const payload: CartType = {
              _id: combo._id,
              menuItemId: combo.menuItemId,
              quantity: combo.quantity,
              maxQty: combo.maxQty,
              promoId: combo.promoId,
              price: combo.price,
              totalCutomizedItemPrice: _.sumBy(
                _.filter(
                  filteredItems.flatMap((i) => i.customizations || []),
                  ['modCodeId', 1]
                ),
                'price'
              ),
              caloricValue: _.sumBy(filteredItems, 'caloricValue'),
              imagePath: combo.imagePath,
              type: Enums.comboType.quickCombo,
              swappedPrice: 0,
              name: combo.name,
              comboSize: combo.comboSize,
              items: filteredItems,
              comboLevelId: combo.comboLevelId,
              refId: combo.refId ? categoryItem.refId : 0,
              menuId: combo.menuId ? categoryItem.menuId : null,
              isMandatory:
                itemsData.data.items &&
                  itemsData.data.items[0] &&
                  itemsData.data.items[0].isMandatory
                  ? itemsData.data.items[0].isMandatory
                  : false,
            };

            if (isAddToCartAllowed()) {
              if (
                config.canShowCustomizations ||
                (itemsData.data.items &&
                  itemsData.data.items[0] &&
                  itemsData.data.items[0].isMandatory)
              ) {
                await getCustomizeItems({
                  item: payload,
                  siteId: selectedSiteId,
                  isDelivery: isDelivery,
                  dispatch: dispatch,
                  itemCustomizations: itemCustomizations,
                  navigate: navigate,
                  isMandatoryCustomization:
                    itemsData.data.items &&
                    itemsData.data.items[0] &&
                    itemsData.data.items[0].isMandatory,
                  showSkip: false,
                  isUpdateCart: false,
                });
              } else {
                dispatch(addToCart(payload));
                dispatch(actions.setScreen(TabScreens.Order));
                navigate(Routes.TabNavigator);
              }
            } else {
              setError(
                `Sorry, you cannot add ${payload.name} more than ${categoryItem?.maxQty} times`
              );
            }
          }
          setIsComboLevelLoading(false);
          return;
        }
        setCombolevelDialogOpen(true);
      }
      setIsComboLevelLoading(false);
    }
  };

  const renderLoader = (): JSX.Element | null => {
    if (!isComboLevelLoading) return null;
    return (
      <div
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0,0,0,0.25)',
          zIndex: 9999,
          display: 'grid',
          placeItems: 'center',
        }}
      >
        <components.Loader />
      </div>
    );
  };

  const createAddToCartPayload = (): CartType | null => {
    if (!categoryItem) {
      return null;
    }
    const payload: CartType = {
      _id: uuidv4(),
      menuItemId: categoryItem.menuItemId,
      salesItemId: categoryItem.salesItemId,
      posItemId: categoryItem.posItemId,
      quantity: 1,
      price: categoryItem.price,
      totalCutomizedItemPrice: 0,
      caloricValue: categoryItem.caloricValue,
      imagePath: categoryItem.imagePath,
      type: Enums.comboType.menuItem,
      name: categoryItem.name,
      maxQty: categoryItem.maxQty,
      freeModifiers: categoryItem.freeModifiers,
      isBreakfast: categoryItem.isBreakfast,
      menuId: categoryItem.menuId,
      isMandatory: categoryItem.isMandatory || false
    };
    return payload;
  };

  const isAddToCartAllowed = () => {
    if (!categoryItem) return false;
    if (categoryItem.maxQty) {
      const matchingItemQty = _.sumBy(_.filter(cartItems, item => item.menuItemId === categoryItem.menuItemId), 'quantity');
      return matchingItemQty < categoryItem.maxQty;
    }
    return true;
  };

  return (
    <>
      {renderLoader()}
      <div style={{ padding: 6 }}>
        <ul>
          <li
            style={{
              height: 100,
              borderRadius: 10,
              padding: "14px 14px",
              backgroundColor: "var(--white-color)",
              boxShadow: "0 2px 0px rgba(0,0,0,0.15)",
              gap: 14,
              position: "relative",
            }}
            className='row-center'
          >
            <img
              src={utils.getImagePath(categoryItem.imagePath)}
              alt={categoryItem.name}
              style={{
                width: 75,
                height: 70,
                borderRadius: 10,
                marginRight: 10,
              }}
            />
            <div style={{ display: "flex", flexDirection: "column" }}>
              <span
                className="t16"
                style={{
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  marginBottom: 4,
                  color: 'var(--main-color)',
                  textTransform: 'capitalize',
                }}
              >
                {categoryItem.name}
              </span>
              <p
                className="number-of-lines-2 t10"
                style={{
                  fontSize: 10,
                  color: "var(--text-color)",
                  lineHeight: 1.5,
                  marginBottom: 4,
                }}
              >
                {categoryItem.description}
              </p>
              {(isShowCalory && categoryItem.calory) ?
                <span className="t10" style={{ marginBottom: 8 }}>
                  {categoryItem.calory}kJ
                </span> : null}
              <span
                className="t14"
                style={{
                  color: "var(--text-color)",
                }}
              >
                {utils.formatCurrency(categoryItem.price)}
              </span>
            </div>
            <button
              style={{
                padding: 14,
                position: "absolute",
                right: 0,
                top: 0,
                borderRadius: 4,
              }}
            >
            </button>
            <button
              style={{
                position: 'absolute',
                right: 0,
                bottom: 0,
                padding: 14,
                borderRadius: 4,
              }}
              onClick={() => onAddCart()}
            >
              < svg.AddSvg />
            </button>
          </li>
        </ul>
        {
          combolevelDialogOpen &&
          <ComboLevelDialog
            mainText="Proceed"
            isOpen={combolevelDialogOpen}
            secondaryText="Cancel"
            title='Please Choose'
            comboLevelId={0}
            comboSize={'S'}
            onClose={() => setCombolevelDialogOpen(false)}
            comboLevels={comboLevels}
            onComboLevelDetails={(selectedCombo) => {
              setComboItem(prev => ({
                ...prev,
                comboSize: selectedCombo.comboSize,
                comboLevelId: selectedCombo.comboLevelId,
                price: selectedCombo.price,
              }));
              getComboItems(selectedCombo.comboLevelId);
            }}
            onApply={() => onPressComboAddToCart()}
          />
        }
      </div >
    </>
  );
};
